import {
  Agent,
  CheckoutSession,
  DocumentStatus,
  getLoanById,
  LenderName,
  Loan,
  OperativeType,
  PaymentIntent,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';

import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import { CardOnboardingPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/CardOnboarding/entities/PaymentMethod/CardOnboardingPaymentMethod';
import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { ActionsMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/CardOnboarding/NextActions/maps/actions.map';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';

// Card Onboarding is a conditional-less loan with near-identical onboarding to consumer loan
// (docs, KYC, signature, MerchantPos, NextActions). Extending LoanPaymentMethodDetailEntity
// deliberately reuses that loan-specific API (refreshDataFromLoan, docs/KYC, isActionCompleted,
// sync) instead of AbstractLoanPaymentMethodDetailEntity (the sibling parent used by BNPL).
// Product differences after signature (validation / print-card next steps) live on this class
// and in CardOnboarding NextActions — not by changing the parent.
export class CardOnboardingPaymentMethodDetailEntity extends LoanPaymentMethodDetailEntity {
  static allowed = [PaymentMethodType.CARD_ONBOARDING];

  constructor(
    paymentMethodType: PaymentMethodType,
    checkoutSession: CheckoutSession,
    agent: Agent,
    paymentIntent?: PaymentIntent,
  ) {
    super(paymentMethodType, checkoutSession, agent, paymentIntent);
  }

  public static isAllowed(type: PaymentMethodType): boolean {
    return this.allowed.includes(type);
  }

  public get operativeType(): OperativeType | null | undefined {
    const paymentMethod = this.paymentMethodOptions;

    if (!paymentMethod || paymentMethod.type !== PaymentMethodType.CARD_ONBOARDING) {
      return undefined;
    }

    return paymentMethod.options?.operativeType;
  }

  public async refresh(): Promise<this> {
    if (!this.paymentMethodId) {
      if (!this.paymentMethod) {
        this.setPaymentMethodFromCalculator();
      }
      this._isRefreshed = true;
      return this;
    }

    try {
      const loan = await getLoanById({ loanId: this.paymentMethodId });
      await this.refreshDataFromLoan(loan);
    } catch (error) {
      this.setError(error as Error);
    }

    this._isRefreshed = true;
    return this;
  }

  public async refreshDataFromLoan(loan: Loan): Promise<this> {
    await super.refreshDataFromLoan(loan);
    this.actions = (await ActionsMap.fromPaymentMethodDetail(this)) ?? undefined;
    return this;
  }

  public async isActionCompleted(action: Action): Promise<boolean> {
    if (action === Action.VALIDATE_CARD_DATA) {
      return this.areCardDocumentsValidated();
    }

    if (action === Action.PRINT_CARD) {
      return this.isConfirmed();
    }

    return super.isActionCompleted(action);
  }

  public areCardDocumentsValidated(): boolean {
    const requiredFiles = this.borrowerDocumentationRequired?.requiredFiles;

    if (!requiredFiles || requiredFiles.length === 0) {
      return false;
    }

    return requiredFiles.every((file) => file.status === DocumentStatus.VALIDATED);
  }

  public setPaymentMethodFromCalculator(): void {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type);

    if (!(paymentMethodEntity instanceof CardOnboardingPaymentMethod)) {
      throw new Error('Payment method entity is not a card onboarding payment method');
    }

    // Not a product concept for card onboarding (no financed amount); required by loan-shaped setup/condition seeding.
    const totalToFinance = this.totalToFinance();
    paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency);

    const lenderNames = this.getLenderNamesForDisplay();
    if (lenderNames.length > 0) {
      paymentMethodEntity.seedProviderNames(lenderNames);
    }

    this.paymentMethod = paymentMethodEntity;
  }

  protected async setPaymentMethodFromLoan(loan: Loan): Promise<void> {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type) as AbstractLoanPaymentMethod;

    if (!(paymentMethodEntity instanceof CardOnboardingPaymentMethod)) {
      throw new Error('Payment method entity is not a card onboarding payment method');
    }

    let lenderNames: LenderName[] | undefined;

    if (loan.loanApplicationSelected) {
      lenderNames = [loan.loanApplicationSelected.lenderName];
    } else {
      const displayLenderNames = this.getLenderNamesForDisplay();
      lenderNames = displayLenderNames.length > 0 ? displayLenderNames : undefined;
    }

    // Not a product concept for card onboarding (no financed amount); required by loan-shaped setup/condition seeding.
    const totalToFinance = this.totalToFinance();
    paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency);
    paymentMethodEntity.setConditionFromLoanConditions(loan, lenderNames);

    this.paymentMethod = paymentMethodEntity;
  }

  private getLenderNamesForDisplay(): LenderName[] {
    const processingStrategyLenderNames = this.getLenderNamesFromProcessingStrategy();
    if (processingStrategyLenderNames.length > 0) {
      return processingStrategyLenderNames;
    }

    const providerName = this.getPaymentIntent()?.providerName;
    if (providerName) {
      return [providerName as LenderName];
    }

    return [];
  }

  public clone(): this {
    const clone = new CardOnboardingPaymentMethodDetailEntity(this.type, this.checkoutSession, this.agent);

    Object.assign(clone, this);

    return clone as this;
  }
}
